import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import { cn } from '@repo/ui/utils'
import {
	createFileRoute,
	type SearchSchemaInput,
	stripSearchParams
} from '@tanstack/react-router'
import { CloudOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from '@/lib/i18n'
import { DetailsPanel } from '@/modules/file-manager/components/details-panel'
import { DetailsPanelToggle } from '@/modules/file-manager/components/details-panel-toggle'
import { FilteredEmptyState } from '@/modules/file-manager/components/filtered-empty-state'
import { ListingGrid } from '@/modules/file-manager/components/listing-grid'
import {
	ListingError,
	ListingState
} from '@/modules/file-manager/components/listing-states'
import { ListingViewSwitch } from '@/modules/file-manager/components/listing-view-switch'
import { setDetailsPanel } from '@/modules/file-manager/hooks/use-details-panel'
import { useListingView } from '@/modules/file-manager/hooks/use-listing-view'
import { useSelectionState } from '@/modules/file-manager/hooks/use-selection-state'
import {
	type FolderSearch,
	readCategories
} from '@/modules/file-manager/lib/folder-search'
import { useCursorList } from '@/modules/file-manager/query/use-cursor-list'
import { GlobalSearch } from '@/modules/search/components/global-search'
import { EmptyTrashButton } from '@/routes/(private)/(shell)/trash/-components/empty-trash-button'
import { EmptyTrashDialog } from '@/routes/(private)/(shell)/trash/-components/empty-trash-dialog'
import { PermanentDeleteDialog } from '@/routes/(private)/(shell)/trash/-components/permanent-delete-dialog'
import { RestoreNameConflictDialog } from '@/routes/(private)/(shell)/trash/-components/restore-name-conflict-dialog'
import { RestoreOriginTrashedDialog } from '@/routes/(private)/(shell)/trash/-components/restore-origin-trashed-dialog'
import { TRASH_COLUMNS } from '@/routes/(private)/(shell)/trash/-components/trash-columns'
import { TrashDetails } from '@/routes/(private)/(shell)/trash/-components/trash-details'
import { TrashEmptyState } from '@/routes/(private)/(shell)/trash/-components/trash-empty-state'
import { TrashFilters } from '@/routes/(private)/(shell)/trash/-components/trash-filters'
import { TrashItemActions } from '@/routes/(private)/(shell)/trash/-components/trash-item-actions'
import { TrashSelectionBar } from '@/routes/(private)/(shell)/trash/-components/trash-selection-bar'
import { TrashSubtitle } from '@/routes/(private)/(shell)/trash/-components/trash-subtitle'
import { useEmptyTrash } from '@/routes/(private)/(shell)/trash/-hooks/use-empty-trash'
import { usePermanentDeleteAction } from '@/routes/(private)/(shell)/trash/-hooks/use-permanent-delete'
import { useRestore } from '@/routes/(private)/(shell)/trash/-hooks/use-restore'
import { trashListQueryOptions } from '@/routes/(private)/(shell)/trash/-query/query-options'

export const Route = createFileRoute('/(private)/(shell)/trash/')({
	component: TrashPage,
	// Canonical on the way out — categories in one order, and absent when
	// empty — the way the listings build theirs (`folder-search.ts`).
	search: {
		middlewares: [
			({ next, search }) => next(readTrashSearch(search)),
			stripSearchParams<TrashSearch>({ categories: [] })
		]
	},
	staticData: {
		breadcrumb: 'client:trash.page_title'
	},
	validateSearch: validateTrashSearch
})

/**
 * «Cestino» (OPSSTD-8393): what the caller could restore, where it came
 * from, when and by whom it was binned, and how much it weighs. From here:
 * restore a selection, destroy it for good, or empty the whole bin — every
 * one of the last two behind a dialog that says what is lost before it
 * happens.
 *
 * **A flat list, not a folder.** The sidebar entry is «piatta» by design
 * (`nav-links.tsx`) and so is this page: `GET /trash` returns everything the
 * caller could restore in one page-able list, ordered by when it was binned,
 * with no folder to descend into — a trashed folder's own contents travel
 * with it rather than being listed underneath it.
 *
 * **The same listing the folder pages draw.** `ListingGrid`, its two views,
 * its selection gestures, its menu on the `⋯` and on the right click, and the
 * details panel beside it: the bin composes them with its own columns, its own
 * menu entries and a panel body drawn from its own rows. What it leaves out is
 * what a trashed node cannot do — it is not dragged or sorted, and opening it
 * opens the details panel rather than the item, so the
 * row of controls holds the type filter, the view switch and the panel toggle
 * and nothing that would offer those.
 *
 * **The type filter is the listings' own**, `CategoryFilter` over the same
 * `categories` parameter, and the server filters: `GET /trash` takes it, so a
 * filtered bin pages like an unfiltered one instead of hiding rows it loaded.
 *
 * **Realtime keeps this page honest without a subscription of its own.**
 * `directory-events.ts` maps `node.trashed`, `node.restored` and
 * `node.purged` onto this page's own query key, so another tab's action, or
 * this one's, invalidates the listing the same way any other directory
 * change does — there is nothing to wire here beyond the query key existing.
 */
function TrashPage() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const { pages, query } = useCursorList(
		trashListQueryOptions(search.categories)
	)
	const items = pages?.flatMap((page) => page.data) ?? []

	const view = useListingView()
	const selection = useSelectionState(items)
	const selectedItems = items.filter((item) =>
		selection.selected.has(item.node.id)
	)
	const landed = useLandOnSelected(
		search.selected,
		items,
		selection.replaceSelection
	)

	useEffect(() => {
		// The row is only in the document if the virtualizer drew it, which it
		// does for the first screenful — where a recently binned search hit
		// almost always is. Further down, the row is picked but not scrolled to.
		if (landed !== null) {
			document
				.querySelector(`[data-node-id="${CSS.escape(landed)}"]`)
				?.scrollIntoView({ block: 'center' })
		}
	}, [landed])

	const restore = useRestore()
	const permanentDelete = usePermanentDeleteAction()
	const emptyTrash = useEmptyTrash()

	const { refusal } = restore
	const conflictDetails =
		refusal &&
		typeof refusal.details === 'object' &&
		refusal.details !== null
			? refusal.details
			: null
	const suggestedName =
		typeof conflictDetails?.suggestedName === 'string'
			? conflictDetails.suggestedName
			: null
	const originalParentId =
		typeof conflictDetails?.originalParentId === 'string'
			? conflictDetails.originalParentId
			: null
	// The origin folder is, itself, something the caller could restore, so it
	// is very often a sibling row already loaded on this same page — read
	// from there rather than from a second request this route has no read
	// for (a trashed node's own detail answers `404`, not a name, per
	// OPSSTD-8375).
	const originalParentName =
		items.find((item) => item.node.id === originalParentId)?.node.name ??
		t('client:trash.restore.origin_trashed.unnamed_folder')

	return (
		<>
			<PageLayout
				aside={
					<DetailsPanel>
						<TrashDetails
							items={items}
							onDelete={(item) => permanentDelete.request([item])}
							onRestore={(item) => restore.request([item])}
							selected={selection.selected}
						/>
					</DetailsPanel>
				}
				bar={
					<div className='flex items-center gap-3'>
						<div className='min-w-0 flex-1'>
							{/* Filters stay below `md` while something is picked: see
						    `folder-bar.tsx`. */}
							<div
								className={cn(
									selectedItems.length > 0 && 'md:hidden'
								)}
							>
								<TrashFilters />
							</div>
							{selectedItems.length > 0 && (
								<div className='max-md:hidden'>
									<TrashSelectionBar
										items={selectedItems}
										onClear={selection.clear}
										onDelete={() =>
											permanentDelete.request(
												selectedItems
											)
										}
										onRestore={() =>
											restore.request(selectedItems)
										}
									/>
								</div>
							)}
						</div>
						<EmptyTrashButton
							onClick={emptyTrash.request}
							preview={emptyTrash.preview}
						/>
						<ListingViewSwitch />
						<DetailsPanelToggle />
					</div>
				}
				breadcrumb={<QueryBreadcrumbs />}
				search={<GlobalSearch />}
			>
				<ListingGrid
					columns={TRASH_COLUMNS}
					empty={
						search.categories.length > 0 ? (
							<FilteredEmptyState description='client:files.filters.empty.description_trash' />
						) : (
							<TrashEmptyState />
						)
					}
					error={
						<ListingError
							description={t('client:trash.error.description')}
							onRetry={() => query.refetch()}
							title={t('client:trash.error.title')}
						/>
					}
					items={items}
					menu={(item, surface) => (
						<TrashItemActions
							item={item}
							onDelete={() => permanentDelete.request([item])}
							onPick={() =>
								selection.onItemClick(
									{
										ctrlKey: false,
										metaKey: false,
										shiftKey: false
									},
									item.node.id
								)
							}
							onRestore={() => restore.request([item])}
							selection={
								selectedItems.length > 1 &&
								selection.selected.has(item.node.id)
									? {
											count: selectedItems.length,
											onDelete: () =>
												permanentDelete.request(
													selectedItems
												),
											onRestore: () =>
												restore.request(selectedItems)
										}
									: undefined
							}
						>
							{surface}
						</TrashItemActions>
					)}
					offline={
						<ListingState
							description={t('client:trash.offline.description')}
							icon={<CloudOff />}
							title={t('client:files.offline.title')}
						/>
					}
					onItemClick={selection.onItemClick}
					// A trashed node cannot be opened: its folder cannot be
					// listed and its file cannot be read until it is restored
					// (OPSSTD-6577). Double click and `Enter` open what the bin
					// can say about it instead — the details panel, on it alone.
					onOpen={(item) => {
						selection.onItemClick(
							{ ctrlKey: false, metaKey: false, shiftKey: false },
							item.node.id
						)
						setDetailsPanel(true)
					}}
					onSelectionChange={selection.replaceSelection}
					query={query}
					// The bin is ordered by when things were binned, so folders
					// arrive among the files and two sections would repeat.
					sections={false}
					selected={selection.selected}
					subtitle={(item, selected) => (
						<TrashSubtitle item={item} selected={selected} />
					)}
					view={view}
				/>
			</PageLayout>

			<PermanentDeleteDialog
				isPending={permanentDelete.isPending}
				items={permanentDelete.confirming.items}
				onCancel={permanentDelete.cancel}
				onConfirm={permanentDelete.confirm}
				open={permanentDelete.confirming.open}
			/>

			<EmptyTrashDialog
				isEmptying={emptyTrash.isEmptying}
				onCancel={emptyTrash.cancel}
				onConfirm={emptyTrash.confirm}
				open={emptyTrash.open}
				preview={emptyTrash.preview}
			/>

			<RestoreNameConflictDialog
				isPending={restore.isPending}
				name={restore.refusalName}
				onCancel={restore.skipCurrent}
				onRename={restore.renameCurrent}
				open={
					restore.open &&
					refusal?.code === 'name_taken' &&
					suggestedName !== null
				}
				suggestedName={suggestedName ?? ''}
			/>

			<RestoreOriginTrashedDialog
				isPending={restore.isPending}
				name={restore.refusalName}
				onCancel={restore.skipCurrent}
				onChooseDestination={restore.chooseDestination}
				onRestoreOrigin={() =>
					originalParentId &&
					restore.restoreOriginFirst(originalParentId)
				}
				open={restore.open && refusal?.code === 'parent_trashed'}
				originalParentName={originalParentName}
				questionId={refusal?.nodeId}
			/>
		</>
	)
}

/**
 * Picks the row `?selected=` names, once, the first time it is loaded — and
 * answers which one it picked, so the page can bring it into view.
 *
 * **Once per id, not on every render.** After landing, the selection is the
 * person's again: clicking elsewhere must not snap back to the hit the address
 * still names. A new id in the address — another search hit into the bin — is
 * a new landing. Adjusted while rendering rather than in an effect, which is
 * what React asks for state derived from a change in something else.
 */
function useLandOnSelected(
	selected: string | undefined,
	items: { node: { id: string } }[],
	replaceSelection: (ids: ReadonlySet<string>) => void
) {
	const [landed, setLanded] = useState<string | null>(null)

	if (
		selected !== undefined &&
		selected !== landed &&
		items.some((item) => item.node.id === selected)
	) {
		setLanded(selected)
		replaceSelection(new Set([selected]))
	}

	return landed
}

interface TrashSearch {
	categories: FolderSearch['categories']
	selected?: string
}

/**
 * The bin's own address, read forgivingly the way the listings read theirs.
 *
 * `selected` names the node a caller should land on already picked — the
 * destination `result-destination.ts` builds for a `scope=trash` search hit
 * (OPSSTD-8374). A mistyped or stale id is this page's own problem to report by
 * simply never matching a row, not a reason to refuse the address.
 *
 * `categories` goes through `readCategories`, the one rule the listings and the
 * search results already share: unknown values dropped, repeats collapsed, one
 * fixed order — so one filter is one address and one cache entry.
 */
function validateTrashSearch(search: Partial<TrashSearch> & SearchSchemaInput) {
	return readTrashSearch(search)
}

function readTrashSearch(input: unknown): TrashSearch {
	const raw =
		typeof input === 'object' && input !== null
			? (input as Record<string, unknown>)
			: {}

	return {
		categories: readCategories(raw.categories, []),
		...(typeof raw.selected === 'string' && { selected: raw.selected })
	}
}
